/**
 * Authentication Utilities
 * Centralized functions for user authentication and session management
 * 
 * NOTE: getAuthHeaders is the canonical source - includes both X-User-ID and session token
 */

/**
 * Get current user from localStorage
 * @returns {Object|null} User object or null if not found
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    return null;
  }
};

// One-shot in-flight hydration promise. We dedupe so a burst of API calls
// from a freshly-rendered page only triggers ONE /auth/me round-trip.
let _hydrationPromise = null;

/**
 * Repair `localStorage.user` when it's stale or missing `id` but a
 * `session_token` is present. The fetch is fire-and-forget — first the
 * caller's request goes out without X-User-ID (the backend's Bearer
 * fallback now handles that case); a few hundred ms later the user
 * object is repaired so subsequent calls send the proper header.
 *
 * Triggered exclusively from `getAuthHeaders()` when the bad state is
 * observed. No-ops once the user object is healthy or after the first
 * /auth/me round-trip, regardless of outcome.
 */
const _hydrateUserIfStale = () => {
  if (_hydrationPromise) return; // dedupe across simultaneous callers
  const sessionToken = localStorage.getItem('session_token');
  if (!sessionToken) return; // not logged in — nothing to repair

  // Build the API base the same way the rest of the app does. We don't
  // import `API` from `App` to avoid a circular import (App imports auth).
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  _hydrationPromise = fetch(`${apiBase}/auth/me`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((user) => {
      if (user?.id) {
        localStorage.setItem('user', JSON.stringify(user));
      }
    })
    .catch(() => {
      // Network or 401 — leave localStorage alone; a real auth failure
      // will surface on the next protected call and route users to /login
      // through the existing 401-handling paths.
    })
    .finally(() => {
      // Allow one more hydration attempt later if it failed, but not in
      // a tight loop — wait 30s before re-arming. This avoids hammering
      // /auth/me on a flaky network while still self-healing eventually.
      setTimeout(() => {
        _hydrationPromise = null;
      }, 30_000);
    });
};

/**
 * Get authentication headers for API requests
 * Includes both X-User-ID and Authorization token if available
 *
 * Self-healing: when `session_token` is present but `localStorage.user`
 * is missing or has no `id`, we kick off a background /auth/me fetch
 * to repair the user object so subsequent calls carry X-User-ID. This
 * matters because some legacy routes still filter strictly by that
 * header. The current call still goes out without it; backend Bearer
 * fallbacks (e.g. /payment-methods) handle the gap on the first call.
 *
 * @returns {Object} Headers object with authentication credentials
 */
export const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Add user ID header
  let userIdPresent = false;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.id) {
        headers['X-User-ID'] = user.id;
        userIdPresent = true;
      }
    }
  } catch (_e) {
    // Corrupted JSON — fall through to hydration below.
  }

  // Add session token if available
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  // Self-heal stale user objects. Cheap no-op when the state is healthy.
  if (sessionToken && !userIdPresent) {
    _hydrateUserIfStale();
  }

  return headers;
};

/**
 * Check if user is currently logged in
 * @returns {boolean} True if user is logged in
 */
export const isAuthenticated = () => {
  return getCurrentUser() !== null;
};

/**
 * Get current user ID
 * @returns {string|null} User ID or null if not logged in
 */
export const getCurrentUserId = () => {
  const user = getCurrentUser();
  return user?.id || null;
};

/**
 * Save user to localStorage
 * @param {Object} user - User object to save
 */
export const saveUser = (user) => {
  try {
    localStorage.setItem('user', JSON.stringify(user));
  } catch (error) {
  }
};

/**
 * Save session token to localStorage
 * @param {string} token - Session token to save
 */
export const saveSessionToken = (token) => {
  try {
    localStorage.setItem('session_token', token);
  } catch (error) {
  }
};

/**
 * Remove user and session from localStorage (logout)
 */
export const clearUser = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('session_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('userSettings');
  // Notify any module-level data stores (e.g. useVendors) to drop their
  // cached lists so the next user can't read the previous user's private
  // data out of memory. Listeners self-subscribe to avoid a circular
  // import between auth.js and the hook files.
  try {
    window.dispatchEvent(new CustomEvent('auth:user-cleared'));
  } catch (_e) {
    // No window (SSR/tests) — safe to ignore.
  }
};

/**
 * Check if user has admin privileges
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  const user = getCurrentUser();
  return user?.is_admin === true;
};
